/* eslint-disable react/prop-types */
import classNames from 'classnames';
import React, { useCallback } from 'react';
import { Form } from 'react-bootstrap';
import {
  useTable,
  useSortBy,
  usePagination,
  useRowSelect,
  useGlobalFilter
} from 'react-table';

export const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, className, ...rest }, ref) => {
    const defaultRef = React.useRef();

    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return (
      <Form.Check
        type="checkbox"
        className={classNames('form-check fs-0 mb-0', className)}
      >
        <Form.Check.Input type="checkbox" ref={resolvedRef} {...rest} />
      </Form.Check>
    );
  }
);

const AdvanceTableWrapper = ({
  children,
  columns,
  data,
  sortable,
  selection,
  selectionColumnWidth,
  pagination,
  perPage = 10,
  // Opt-in server-side mode. When manualPagination is true the parent owns
  // pagination + search: pass the current page of rows as `data`, the total
  // page count as controlledPageCount, and receive {pageIndex, pageSize,
  // globalFilter} via onServerStateChange to refetch. Defaults keep every
  // other table fully client-side and unchanged.
  manualPagination = false,
  controlledPageCount,
  onServerStateChange,
  // Server mode only: where to start (restoring a previous visit)
  initialPageIndex = 0,
  initialGlobalFilter = ''
}) => {
  const getRowId = useCallback(row => {
    return row.id;
  }, []);
  const {
    getTableProps,
    headers,
    page,
    rows,
    prepareRow,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    setPageSize,
    gotoPage,
    pageCount,
    state: { pageIndex, pageSize, selectedRowIds, globalFilter },
    setGlobalFilter
  } = useTable(
    {
      columns,
      data,
      disableSortBy: !sortable,
      initialState: {
        ...(pagination ? { pageSize: perPage } : {}),
        ...(manualPagination
          ? {
              pageIndex: initialPageIndex || 0,
              globalFilter: initialGlobalFilter || undefined
            }
          : {})
      },
      getRowId,
      manualPagination,
      manualGlobalFilter: manualPagination,
      autoResetPage: !manualPagination,
      autoResetGlobalFilter: !manualPagination,
      autoResetSortBy: !manualPagination,
      ...(manualPagination && typeof controlledPageCount === 'number'
        ? { pageCount: controlledPageCount }
        : {})
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    hooks => {
      if (selection) {
        hooks.visibleColumns.push(columns => [
          {
            id: 'selection',
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
            ),
            headerProps: {
              style: {
                width: selectionColumnWidth
              }
            },
            cellProps: {
              className: 'py-2',
              style: {
                width: selectionColumnWidth
              }
            },
            Cell: ({ row }) => (
              <div onClick={e => e?.stopPropagation()}>
                <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
              </div>
            )
          },
          ...columns
        ]);
      }
    }
  );

  // In server mode, tell the parent when the page or search term changes so it
  // can refetch. A new search term resets back to the first page.
  const prevGlobalFilter = React.useRef(globalFilter);
  React.useEffect(() => {
    if (!manualPagination) return;
    if (prevGlobalFilter.current !== globalFilter) {
      prevGlobalFilter.current = globalFilter;
      if (pageIndex !== 0) {
        gotoPage(0);
        return;
      }
    }
    if (onServerStateChange) {
      onServerStateChange({ pageIndex, pageSize, globalFilter });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualPagination, pageIndex, pageSize, globalFilter]);

  const recursiveMap = children => {
    return React.Children.map(children, child => {
      if (child.props?.children) {
        return React.cloneElement(child, {
          children: recursiveMap(child.props.children)
        });
      } else {
        if (child.props?.table) {
          return React.cloneElement(child, {
            ...child.props,
            getTableProps,
            headers,
            page,
            // rows = after global search filtering, before paging
            filteredRowCount: rows.length,
            prepareRow,
            canPreviousPage,
            canNextPage,
            nextPage,
            previousPage,
            gotoPage,
            pageCount,
            pageIndex,
            selectedRowIds,
            pageSize,
            setPageSize,
            globalFilter,
            setGlobalFilter
          });
        } else {
          return child;
        }
      }
    });
  };

  return (
    // <>
    //   {React.Children.map(children, child => {
    //     if (child.props.table) {
    //       return React.cloneElement(child, {
    //         ...child.props,
    //         getTableProps,
    //         headers,
    //         page,
    //         prepareRow,
    //         canPreviousPage,
    //         canNextPage,
    //         nextPage,
    //         previousPage,
    //         gotoPage,
    //         pageCount,
    //         pageIndex,
    //         selectedRowIds,
    //         pageSize,
    //         setPageSize
    //       });
    //     } else {
    //       return child;
    //     }
    //   })}
    // </>
    <>{recursiveMap(children)}</>
  );
};

export default AdvanceTableWrapper;
